import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { User, Calendar, MapPin, GraduationCap, Fingerprint, Clock, ArrowRight, ExternalLink } from "lucide-react";
import { StudentBarcodes } from "./StudentBarcodes";
import type { Tables } from "@/integrations/supabase/types";

type Student = Tables<"students">;

interface StudentProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student | null;
}

function getProgram(cls: string | null): string {
  if (!cls) return "—";
  const prefix = cls.toUpperCase();
  if (prefix.startsWith("CMT")) return "Clinical Medicine";
  if (prefix.startsWith("PST")) return "Pharmaceutical Sciences";
  if (prefix.startsWith("SW")) return "Social Work";
  return "—";
}

function formatStatus(status: string) {
  return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

export function StudentProfileDialog({ open, onOpenChange, student }: StudentProfileDialogProps) {
  const navigate = useNavigate();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [fingerprintUrl, setFingerprintUrl] = useState<string | null>(null);
  const [loadingBio, setLoadingBio] = useState(false);
  const [statusHistory, setStatusHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!open || !student) {
      setPhotoUrl(null);
      setSignatureUrl(null);
      setFingerprintUrl(null);
      setStatusHistory([]);
      return;
    }

    (async () => {
      setLoadingBio(true);
      const { data } = await supabase
        .from("student_biometrics")
        .select("*")
        .eq("student_id", student.id)
        .maybeSingle();

      if (data) {
        const [photo, sig, fp] = await Promise.all([
          data.photo_path ? getSignedFileUrl(data.photo_path) : null,
          data.signature_path ? getSignedFileUrl(data.signature_path) : null,
          data.fingerprint_path ? getSignedFileUrl(data.fingerprint_path) : null,
        ]);
        setPhotoUrl(photo);
        setSignatureUrl(sig);
        setFingerprintUrl(fp);
      }
      setLoadingBio(false);
    })();

    // Fetch status history
    (async () => {
      setLoadingHistory(true);
      const { data: history } = await supabase
        .from("student_status_history")
        .select("*")
        .eq("student_id", student.id)
        .order("created_at", { ascending: true });
      setStatusHistory(history || []);
      setLoadingHistory(false);
    })();
  }, [open, student]);

  if (!student) return null;

  const extStudent = student as Student & {
    current_semester?: string | null;
    barcode_admin?: string | null;
    barcode_academic?: string | null;
    barcode_examination?: string | null;
    barcode_accounting?: string | null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" /> Student Profile
          </DialogTitle>
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1"
            onClick={() => { onOpenChange(false); navigate(`/student-file/${student.id}`); }}
          >
            <ExternalLink className="h-3.5 w-3.5" /> View Full File
          </Button>
        </DialogHeader>

        <div className="space-y-5">
          {/* Photo + Identity */}
          <div className="flex gap-4 items-start">
            {photoUrl ? (
              <img src={photoUrl} alt="Student" className="w-[105px] h-[135px] rounded-md object-cover border" />
            ) : (
              <div className="w-[105px] h-[135px] rounded-md border bg-muted flex items-center justify-center text-muted-foreground">
                <User className="h-8 w-8" />
              </div>
            )}
            <div className="space-y-1">
              <h3 className="text-lg font-semibold">{student.first_name} {student.last_name}</h3>
              <p className="text-sm text-muted-foreground font-mono">{student.student_number}</p>
              <Badge variant="outline">{formatStatus(student.status)}</Badge>
            </div>
          </div>

          <Separator />

          {/* Academic Info */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <GraduationCap className="h-4 w-4" /> Academic Information
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div><span className="text-muted-foreground">Class:</span> {student.class || "—"}</div>
              <div><span className="text-muted-foreground">Program:</span> {getProgram(student.class)}</div>
              <div><span className="text-muted-foreground">NTA Level:</span> {student.nta_level || "—"}</div>
              <div><span className="text-muted-foreground">Sit Status:</span> {student.sit_status ? formatStatus(student.sit_status) : "—"}</div>
              <div><span className="text-muted-foreground">Semester:</span> {extStudent.current_semester || "—"}</div>
              <div><span className="text-muted-foreground">Form IV Index:</span> {student.form_iv_index || "—"}</div>
              <div><span className="text-muted-foreground">NACTEVET Reg #:</span> {student.nactevet_reg_number || "—"}</div>
              <div><span className="text-muted-foreground">Admission:</span> {student.admission_status ? formatStatus(student.admission_status) : "—"}</div>
            </div>
          </div>

          <Separator />

          {/* Personal Info */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> Personal Details
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div><span className="text-muted-foreground">Gender:</span> {student.gender ? formatStatus(student.gender) : "—"}</div>
              <div><span className="text-muted-foreground">Date of Birth:</span> {student.date_of_birth || "—"}</div>
              <div><span className="text-muted-foreground">Email:</span> {student.email || "—"}</div>
              <div><span className="text-muted-foreground">Phone:</span> {student.phone || "—"}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Registration Date:</span> {student.enrollment_date || "—"}</div>
            </div>
          </div>

          {student.address && (
            <>
              <Separator />
              <div className="space-y-1">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> Address
                </h4>
                <p className="text-sm">{student.address}</p>
              </div>
            </>
          )}

          <Separator />

          {/* Financial */}
          <div className="space-y-1">
            <h4 className="text-sm font-semibold">Financial Status</h4>
            <div className="text-sm">
              <Badge variant={student.financial_clearance ? "default" : "destructive"}>
                {student.financial_clearance_status ? formatStatus(student.financial_clearance_status) : "Not Cleared"}
              </Badge>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <Clock className="h-4 w-4" /> Status History
            </h4>
            {loadingHistory ? (
              <p className="text-xs text-muted-foreground">Loading history...</p>
            ) : statusHistory.length === 0 ? (
              <p className="text-xs text-muted-foreground">No status history recorded yet</p>
            ) : (
              <div className="relative pl-4 space-y-3">
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
                {statusHistory.map((entry, idx) => (
                  <div key={entry.id} className="relative flex items-start gap-3">
                    <div className={`absolute left-[-13px] top-1 h-2.5 w-2.5 rounded-full border-2 ${idx === statusHistory.length - 1 ? 'bg-primary border-primary' : 'bg-background border-muted-foreground/40'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {entry.old_status ? (
                          <>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{formatStatus(entry.old_status)}</Badge>
                            <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                          </>
                        ) : null}
                        <Badge variant={idx === statusHistory.length - 1 ? "default" : "outline"} className="text-[10px] px-1.5 py-0">
                          {formatStatus(entry.new_status)}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {new Date(entry.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        {" · "}
                        {new Date(entry.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        {!entry.old_status && " · Initial status"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Biometrics */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-1.5">
              <Fingerprint className="h-4 w-4" /> Biometric & Digital Details
            </h4>
            {loadingBio ? (
              <p className="text-xs text-muted-foreground">Loading biometrics...</p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center space-y-1">
                  <p className="text-xs text-muted-foreground">Passport Photo</p>
                  {photoUrl ? (
                    <img src={photoUrl} alt="Passport" className="h-20 w-[60px] rounded border object-cover mx-auto" />
                  ) : (
                    <div className="h-20 w-[60px] rounded border bg-muted flex items-center justify-center text-xs text-muted-foreground mx-auto">N/A</div>
                  )}
                </div>
                <div className="text-center space-y-1">
                  <p className="text-xs text-muted-foreground">Signature</p>
                  {signatureUrl ? (
                    <img src={signatureUrl} alt="Signature" className="h-20 rounded border bg-background mx-auto object-contain" />
                  ) : (
                    <div className="h-20 rounded border bg-muted flex items-center justify-center text-xs text-muted-foreground">N/A</div>
                  )}
                </div>
                <div className="text-center space-y-1">
                  <p className="text-xs text-muted-foreground">Fingerprint</p>
                  {fingerprintUrl ? (
                    <img src={fingerprintUrl} alt="Fingerprint" className="h-20 w-20 rounded border object-cover mx-auto" />
                  ) : (
                    <div className="h-20 w-20 rounded border bg-muted flex items-center justify-center text-xs text-muted-foreground mx-auto">N/A</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <Separator />

          {/* Barcodes */}
          <StudentBarcodes
            studentName={`${student.first_name} ${student.last_name}`}
            barcodeAdmin={extStudent.barcode_admin || null}
            barcodeAcademic={extStudent.barcode_academic || null}
            barcodeExamination={extStudent.barcode_examination || null}
            barcodeAccounting={extStudent.barcode_accounting || null}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
